/**
 * @spec docs/specs/features/staking-position-ui.md#data-fetching
 * Hook for fetching token balances for any address using public RPC
 */
import { useQuery } from '@tanstack/react-query';
import { ethers } from 'ethers';
import { env } from '@/lib/env';
import { getTokenContract } from '@/lib/contracts';
import { getBaseProvider } from '@/lib/provider';

/**
 * Get PHNM token address from env
 */
function getPHNMAddress(): string {
  const address = env.contracts.phnmTokenAddress;
  if (!address) {
    throw new Error('PHNM token address not configured');
  }
  return address;
}

export interface TokenBalanceData {
  /**
   * Raw balance in wei
   */
  balance: bigint;

  /**
   * Token symbol
   */
  symbol: string;

  /**
   * Token decimals
   */
  decimals: number;
}

/**
 * Hook for fetching PHNM balance for any address
 *
 * Uses public RPC provider so it works for viewing any address,
 * not just the connected wallet.
 */
export function usePHNMBalancePublic(address: string | null) {
  return useQuery({
    queryKey: ['phnm-balance-public', address],
    queryFn: async (): Promise<TokenBalanceData> => {
      if (!address) {
        return { balance: 0n, symbol: 'PHNM', decimals: 18 };
      }

      const provider = getBaseProvider();
      const tokenAddress = getPHNMAddress();
      const tokenContract = getTokenContract(tokenAddress, provider);

      const [balance, symbol, decimals] = await Promise.all([
        tokenContract.balanceOf(address) as Promise<bigint>,
        tokenContract.symbol() as Promise<string>,
        tokenContract.decimals() as Promise<number>,
      ]);

      return {
        balance,
        symbol,
        decimals: Number(decimals),
      };
    },
    enabled: !!address,
    staleTime: 30000, // 30 seconds - balance doesn't change that often
    refetchInterval: 60000, // Refetch every minute
  });
}

/**
 * Hook for fetching native ETH balance for any address
 */
export function useETHBalance(address: string | null) {
  return useQuery({
    queryKey: ['eth-balance', address],
    queryFn: async (): Promise<TokenBalanceData> => {
      if (!address) {
        return { balance: 0n, symbol: 'ETH', decimals: 18 };
      }

      const provider = getBaseProvider();
      const balance = await provider.getBalance(address);
      return { balance, symbol: 'ETH', decimals: 18 };
    },
    enabled: !!address,
    staleTime: 30000,
    refetchInterval: 60000,
  });
}

/**
 * Hook for fetching native POL balance on Polygon for any address
 */
export function usePolygonNativeBalance(address: string | null) {
  return useQuery({
    queryKey: ['polygon-native-balance', address],
    queryFn: async (): Promise<TokenBalanceData> => {
      if (!address) {
        return { balance: 0n, symbol: 'POL', decimals: 18 };
      }

      const provider = getPolygonProvider();
      const balance = await provider.getBalance(address);
      return { balance, symbol: 'POL', decimals: 18 };
    },
    enabled: !!address,
    staleTime: 30000,
    refetchInterval: 60000,
  });
}

/**
 * Get USDC token address from env
 */
function getUSDCAddress(): string {
  const address = env.contracts.usdcTokenAddress;
  if (!address) {
    throw new Error('USDC token address not configured');
  }
  return address;
}

/**
 * Hook for fetching USDC balance for any address
 *
 * Uses public RPC provider so it works for viewing any address,
 * not just the connected wallet.
 */
export function useUSDCBalancePublic(address: string | null) {
  return useQuery({
    queryKey: ['usdc-balance-public', address],
    queryFn: async (): Promise<TokenBalanceData> => {
      if (!address) {
        return { balance: 0n, symbol: 'USDC', decimals: 6 };
      }

      const provider = getBaseProvider();
      const tokenAddress = getUSDCAddress();
      const tokenContract = getTokenContract(tokenAddress, provider);

      const [balance, symbol, decimals] = await Promise.all([
        tokenContract.balanceOf(address) as Promise<bigint>,
        tokenContract.symbol() as Promise<string>,
        tokenContract.decimals() as Promise<number>,
      ]);

      return {
        balance,
        symbol,
        decimals: Number(decimals),
      };
    },
    enabled: !!address,
    staleTime: 30000, // 30 seconds - balance doesn't change that often
    refetchInterval: 60000, // Refetch every minute
  });
}

/**
 * Get a public provider for Polygon network
 */
function getPolygonProvider(): ethers.JsonRpcProvider {
  const rpcUrl = env.polygonRpcUrl;
  if (!rpcUrl) {
    throw new Error('Polygon RPC URL not configured');
  }
  return new ethers.JsonRpcProvider(rpcUrl);
}

/**
 * Hook for fetching USDT balance on Polygon for any address
 *
 * Uses public RPC provider so it works for viewing any address,
 * not just the connected wallet.
 */
export function useUSDTBalancePublic(address: string | null) {
  return useQuery({
    queryKey: ['usdt-balance-public', address],
    queryFn: async (): Promise<TokenBalanceData> => {
      if (!address) {
        return { balance: 0n, symbol: 'USDT', decimals: env.usdtDecimals };
      }

      const provider = getPolygonProvider();
      const tokenAddress = env.contracts.polygonUsdtAddress;
      if (!tokenAddress) {
        throw new Error('Polygon USDT address not configured');
      }
      const tokenContract = getTokenContract(tokenAddress, provider);

      const [balance, decimals] = await Promise.all([
        tokenContract.balanceOf(address) as Promise<bigint>,
        tokenContract.decimals() as Promise<number>,
      ]);

      return {
        balance,
        symbol: 'USDT',
        decimals: Number(decimals),
      };
    },
    enabled: !!address && !!env.contracts.polygonUsdtAddress,
    staleTime: 30000,
    refetchInterval: 60000,
  });
}

/**
 * Hook for fetching any ERC20 token balance
 *
 * Uses public RPC provider so it works for viewing any address,
 * not just the connected wallet.
 */
export function useTokenBalancePublic(tokenAddress: string | null, userAddress: string | null) {
  return useQuery({
    queryKey: ['token-balance', tokenAddress, userAddress],
    queryFn: async (): Promise<TokenBalanceData> => {
      if (!tokenAddress || !userAddress) {
        return { balance: 0n, symbol: '', decimals: 18 };
      }

      const provider = getBaseProvider();
      const tokenContract = getTokenContract(tokenAddress, provider);

      const [balance, symbol, decimals] = await Promise.all([
        tokenContract.balanceOf(userAddress) as Promise<bigint>,
        tokenContract.symbol() as Promise<string>,
        tokenContract.decimals() as Promise<number>,
      ]);

      return {
        balance,
        symbol,
        decimals: Number(decimals),
      };
    },
    enabled: !!tokenAddress && !!userAddress,
    staleTime: 30000,
    refetchInterval: 60000,
  });
}

/**
 * Get Polygon USDT token address from env
 */
function getPolygonUSDTAddress(): string {
  const address = env.contracts.polygonUsdtAddress;
  if (!address) {
    throw new Error('Polygon USDT token address not configured');
  }
  return address;
}

/**
 * Hook for fetching USDT balance on Polygon for any address
 *
 * Uses Polygon RPC provider to read the USDT contract on Polygon.
 */
export function useUSDTBalancePolygon(address: string | null) {
  return useQuery({
    queryKey: ['usdt-balance-polygon', address],
    queryFn: async (): Promise<TokenBalanceData> => {
      if (!address) {
        return { balance: 0n, symbol: 'USDT', decimals: 6 };
      }

      const provider = getPolygonProvider();
      const tokenAddress = getPolygonUSDTAddress();
      const tokenContract = getTokenContract(tokenAddress, provider);

      const [balance, symbol, decimals] = await Promise.all([
        tokenContract.balanceOf(address) as Promise<bigint>,
        tokenContract.symbol() as Promise<string>,
        tokenContract.decimals() as Promise<number>,
      ]);

      return {
        balance,
        symbol,
        decimals: Number(decimals),
      };
    },
    enabled: !!address,
    staleTime: 30000,
    refetchInterval: 60000,
  });
}
